import type { RcFile } from 'antd/es/upload'

import type { IconPickerValue } from 'src/components'
import { AVATAR_PALETTE } from 'src/components/UserCell/avatarColor'

import type { ExtendedPatchedApplicationUpdate } from './thirdPartyApi'

export type AppLucideIcon = {
  name: string
  color: string
}

const SETTINGS_KEY = 'icon'
const FALLBACK_COLOR = AVATAR_PALETTE[0]

/**
 * `settings` is a free-form JSON blob on the backend, so the stored icon is
 * parsed rather than trusted. It sits under a single key —
 * `{ icon: { name: 'rocket', color: '#7C3AED' } }` — matching how the blob's
 * other tenants (`platform_core`, `local_ui`) each own one namespaced object.
 */
export const readAppLucideIcon = (
  settings: Record<string, unknown> | null | undefined
): AppLucideIcon | null => {
  const stored = settings?.[SETTINGS_KEY]

  if (typeof stored !== 'object' || stored === null) return null

  const { name, color } = stored as Partial<AppLucideIcon>

  if (typeof name !== 'string' || !name) return null

  return {
    name,
    color: typeof color === 'string' && color ? color : FALLBACK_COLOR
  }
}

export const withAppLucideIcon = (
  settings: Record<string, unknown> | null | undefined,
  icon: AppLucideIcon | null
): Record<string, unknown> => ({
  ...settings,
  [SETTINGS_KEY]: icon ? { name: icon.name, color: icon.color } : null
})

export type AppIconSource = {
  photoUrl?: string | null
  settings?: Record<string, unknown> | null
}

export const appIconFromPickerValue = (
  value: IconPickerValue | null | undefined
): AppIconSource => ({
  photoUrl: value?.kind === 'photo' ? value.url : null,
  settings: value?.kind === 'lucide' ? withAppLucideIcon(null, value) : null
})

type AppIconPhotoUploader = (file: RcFile) => Promise<number>

const appIconAttachmentId = async (
  value: IconPickerValue | null | undefined,
  uploadPhoto: AppIconPhotoUploader
): Promise<number | null> => {
  if (value?.kind !== 'photo') return null

  return value.source === 'file' ? uploadPhoto(value.file) : Number(value.id)
}

/**
 * The two backend fields kept mutually exclusive: choosing a glyph clears the
 * uploaded attachment, choosing a photo clears the glyph. `currentSettings`
 * carries the rest of the blob through untouched.
 */
export const appIconPatchBody = async (
  value: IconPickerValue | null | undefined,
  uploadPhoto: AppIconPhotoUploader,
  currentSettings?: Record<string, unknown> | null
): Promise<ExtendedPatchedApplicationUpdate> => ({
  icon: await appIconAttachmentId(value, uploadPhoto),
  settings: withAppLucideIcon(
    currentSettings,
    value?.kind === 'lucide' ? value : null
  )
})

export const appIconPickerValue = (app: {
  iconUrl?: string | null
  iconId?: number | null
  settings?: Record<string, unknown> | null
}): IconPickerValue | null => {
  if (app.iconUrl && app.iconId != null) {
    return { kind: 'photo', source: 'stored', url: app.iconUrl, id: app.iconId }
  }

  const lucide = readAppLucideIcon(app.settings)

  return lucide ? { kind: 'lucide', ...lucide } : null
}
